/*
 * Accessibility fixes for the AudioEye Source Feedback Report (2026-09-27), added 2026-11 by Uniek.
 * Loaded after superstore.js. Each part is idempotent and re-runs when the theme re-renders sections.
 *   #1  hero carousel: hidden slides are inert, slide changes are announced
 *   #6  live search: result counts are announced
 *   #7  filters: focus returns to the ticked checkbox after the collection re-renders
 *   #10 Calculate Shipping: aria-expanded follows the calculator
 *   #13 grid / list view: aria-pressed follows the active button
 *   filter "See more" links: named after their filter group, with aria-expanded
 *   "Follow on Shop" button, installments line and its popup: aria-label on the Shop logo
 *   placeholder images and the hover zoom image: hidden from assistive technology
 *   #5  Instafeed: visible focus (CSS), decorative photos, popup close symbol hidden, Follow link label
 */
(function () {
  'use strict';

  function once (el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  function statusRegion (parent) {
    var region = document.createElement ('div');
    region.className = 'visually-hidden';
    region.setAttribute ('role', 'status');
    region.setAttribute ('aria-live', 'polite');
    region.setAttribute ('aria-atomic', 'true');
    parent.appendChild (region);
    return region;
  }

  // #1 hero carousel
  function initSlideshows () {
    document.querySelectorAll ('.slideshow').forEach (function (show) {
      var slides = show.querySelectorAll ('.slideshow-slide');
      if (!slides.length || !once (show, 'adaSlides')) return;
      var region = statusRegion (show);
      var started = false;

      function sync () {
        var enabled = show.classList.contains ('flickity-enabled');
        slides.forEach (function (slide, i) {
          var current = slide.classList.contains ('is-selected');
          slide.inert = enabled && !current;
          if (current && started) {
            var heading = slide.querySelector ('.slideshow-slide-heading');
            region.textContent = 'Slide ' + (i + 1) + ' of ' + slides.length + (heading ? ': ' + heading.textContent.trim () : '');
          }
        });
        started = enabled;
      }

      var observer = new MutationObserver (sync);
      observer.observe (show, {attributes: true, attributeFilter: ['class']});
      slides.forEach (function (slide) {
        observer.observe (slide, {attributes: true, attributeFilter: ['class']});
      });
      sync ();
    });
  }

  // #6 live search
  function initSearchStatus () {
    document.querySelectorAll ('[data-live-search]').forEach (function (box) {
      var results = box.querySelector ('[data-live-search-results]');
      var input = box.querySelector ('[data-live-search-input]');
      if (!results || !input || !once (box, 'adaSearch')) return;
      var region = statusRegion (box);
      var timer;

      function plural (n, word) {
        return n + ' ' + word + (n === 1 ? '' : 's');
      }

      function update () {
        clearTimeout (timer);
        timer = setTimeout (function () {
          if (!input.value.trim () || !results.classList.contains ('visible')) {
            region.textContent = '';
            return;
          }
          if (results.querySelector ('.search-flydown__no-results')) {
            region.textContent = 'No results found.';
            return;
          }
          var products = results.querySelectorAll ('a.search-flydown__product').length;
          var others = results.querySelectorAll ('.search-flydown__result-list a').length - products;
          var parts = [];
          if (products) parts.push (plural (products, 'product'));
          if (others) parts.push (plural (others, 'suggestion'));
          region.textContent = parts.length ? parts.join (' and ') + ' available.' : '';
        }, 700);
      }

      new MutationObserver (update).observe (results, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
      input.addEventListener ('input', update);
    });
  }

  // #10 Calculate Shipping
  function initShippingToggle () {
    document.querySelectorAll ('.cart-shipping-toggle[aria-controls]').forEach (function (button) {
      var panel = document.getElementById (button.getAttribute ('aria-controls'));
      if (!panel || !once (button, 'adaExpanded')) return;

      function sync () {
        var open = panel.offsetHeight > 0 && getComputedStyle (panel).visibility !== 'hidden';
        button.setAttribute ('aria-expanded', open ? 'true' : 'false');
      }

      button.addEventListener ('click', function () {
        setTimeout (sync, 50);
        setTimeout (sync, 600);   // after the open / close animation
      });
      new MutationObserver (sync).observe (panel, {attributes: true, attributeFilter: ['class', 'style']});
      sync ();
    });
  }

  // #13 grid / list view
  function initViewToggle () {
    document.querySelectorAll ('.utils-viewtoggle-button').forEach (function (button) {
      if (!once (button, 'adaPressed')) return;

      function sync () {
        button.setAttribute ('aria-pressed', button.classList.contains ('active') ? 'true' : 'false');
      }

      new MutationObserver (sync).observe (button, {attributes: true, attributeFilter: ['class']});
      sync ();
    });
  }

  // filter "See more / See less" links: say which filter group they open, and whether it is open
  function initFilterToggles () {
    document.querySelectorAll ('[data-filter-toggle]').forEach (function (link) {
      var list = link.closest ('ul');
      var heading = list && list.previousElementSibling;
      if (!heading || !once (link, 'adaToggle')) return;
      var group = heading.textContent.trim ();

      function sync () {
        var text = link.textContent.trim ();
        link.setAttribute ('aria-label', text + ' ' + group + ' options');
        link.setAttribute ('aria-expanded', /less/i.test (text) ? 'true' : 'false');
      }

      link.setAttribute ('role', 'button');
      new MutationObserver (sync).observe (link, {childList: true, characterData: true, subtree: true});
      sync ();
    });
  }

  // Shopify's "Follow on Shop" button, product page installments line and its "Sample plans" popup (a portal on <body>):
  // their Shop logo has role="img" but only an SVG <title>, so give it an aria-label
  function initShopFollow () {
    document.querySelectorAll ('shop-follow-button, shopify-payment-terms, [data-portal-instance-id]').forEach (function (button) {
      var root = button.shadowRoot;
      if (!root || !once (button, 'adaShop')) return;   // retried on a later run until Shopify attaches the shadow root

      function sync () {
        root.querySelectorAll ('svg[role="img"]:not([aria-label])').forEach (function (svg) {
          var title = svg.querySelector ('title');
          if (title && title.textContent.trim ()) svg.setAttribute ('aria-label', title.textContent.trim ());
        });
      }

      new MutationObserver (sync).observe (root, {childList: true, subtree: true});
      sync ();
    });
  }

  // #5 Instafeed: every photo is decorative (in the feed the named post button covers it; in the popup the caption and
  // the "kateandlaurel" link sit next to it), the popup close "✕" is a symbol, and the Follow link is named by its visible text
  function initInstafeed () {
    var feed = document.getElementById ('insta-feed');
    if (!feed) return;
    // the popups (.instafeed-lightbox) are attached outside the feed
    document.querySelectorAll ('#insta-feed img:not([data-ada-decorative]), .instafeed-lightbox img:not([data-ada-decorative])').forEach (function (img) {
      img.dataset.adaDecorative = '1';
      img.setAttribute ('alt', '');
    });
    document.querySelectorAll ('.instafeed-lightbox .instafeed-close-button:not([data-ada-close])').forEach (function (button) {
      button.dataset.adaClose = '1';
      button.childNodes.forEach (function (node) {
        if (node.nodeType !== 3 || !node.textContent.trim ()) return;
        var span = document.createElement ('span');
        span.setAttribute ('aria-hidden', 'true');
        button.replaceChild (span, node);
        span.appendChild (node);
      });
    });
    document.querySelectorAll ('.instafeed-lightbox a.follow[aria-label]:not([data-ada-follow])').forEach (function (link) {
      link.dataset.adaFollow = '1';
      if (!link.textContent.trim ()) return;
      // the scanner wants the name to equal the visible text, so the name comes from the content, with the new-window note as hidden text
      link.removeAttribute ('aria-label');
      var note = document.createElement ('span');
      note.className = 'visually-hidden';
      note.textContent = ' (Instagram profile, opens in a new window)';
      link.appendChild (note);
    });
  }

  // #7 filters: ticking a filter re-renders the collection and focus fell back to the start of the page,
  // so return it to the same checkbox (ids like filter-Color-1 survive the re-render)
  var filterFocusId = null, filterFocusUntil = 0;
  document.addEventListener ('change', function (e) {
    var box = e.target;
    if (box.matches && box.matches ('.faceted-filter__checkbox') && box.id) {
      filterFocusId = box.id;
      filterFocusUntil = Date.now () + 10000;
    }
  }, true);

  function restoreFilterFocus () {
    if (!filterFocusId || Date.now () > filterFocusUntil) return;
    var active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;   // still on the old checkbox, or moved on
    var box = document.getElementById (filterFocusId);
    if (!box) return;
    filterFocusId = null;
    box.focus ();
  }

  // placeholder images (e.g. the empty one in the hidden add-to-cart banner) are decorative; the scanner counts them as unnamed images
  function initPlaceholders () {
    document.querySelectorAll ('svg.placeholder--image:not([aria-hidden])').forEach (function (svg) {
      svg.setAttribute ('aria-hidden', 'true');
    });
  }

  // the enlarged image the theme adds while hovering a product photo has no alt; it repeats the named photo below it, so it is decorative
  function markZoomImage () {
    document.querySelectorAll ('.drift-zoom-pane img:not([alt])').forEach (function (img) {
      img.setAttribute ('alt', '');
    });
  }

  function initAll () {
    restoreFilterFocus ();
    initPlaceholders ();
    initSlideshows ();
    initSearchStatus ();
    initShippingToggle ();
    initViewToggle ();
    initFilterToggles ();
    initShopFollow ();
    initInstafeed ();
  }

  var pending;
  function initSoon () {
    clearTimeout (pending);
    pending = setTimeout (initAll, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener ('DOMContentLoaded', initAll);
  } else {
    initAll ();
  }
  // sections re-rendered by the theme (filtering, cart updates, theme editor) get the fixes too
  new MutationObserver (function () {
    markZoomImage ();   // at once: the zoom image only exists while the mouse is over the photo
    initSoon ();
  }).observe (document.documentElement, {childList: true, subtree: true});
  // late-loading third-party components (Follow on Shop) may finish after the page stops changing
  window.addEventListener ('load', function () {
    setTimeout (initAll, 2000);
    setTimeout (initAll, 8000);
  });
}) ();
